"use client";

import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ResponsiveDialog } from "@/components/ui/responsive-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { OAuthButtons } from "@/components/oauth-buttons";
import { useAuthModal } from "@/store/auth-modal-store";
import { Gift } from "lucide-react";
import { translateAuthError as translateError } from "@/lib/auth-errors";

// Practical client-side format check. The server still enforces canonical
// validation, but catching obvious typos here saves a round-trip and a
// confusing "invalid email" toast that's actually about a missing dot.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

function validateEmail(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return "Введите email";
  if (trimmed.length > 254) return "Email слишком длинный";
  if (!EMAIL_RE.test(trimmed)) return "Неверный формат email";
  return null;
}

export function AuthModal() {
  const { isOpen, message, onSuccess, close } = useAuthModal();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [showReset, setShowReset] = useState(false);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [resetEmail, setResetEmail] = useState("");

  const loginEmailRef = useRef<HTMLInputElement>(null);
  const registerEmailRef = useRef<HTMLInputElement>(null);
  const resetEmailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setShowReset(false);
    const timer = setTimeout(() => {
      if (activeTab === "login") loginEmailRef.current?.focus();
      else registerEmailRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [isOpen, activeTab]);

  useEffect(() => {
    if (showReset) {
      const timer = setTimeout(() => resetEmailRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [showReset]);

  const handleSuccess = () => {
    close();
    onSuccess?.();
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailError = validateEmail(loginEmail);
    if (emailError) {
      toast.error(emailError);
      loginEmailRef.current?.focus();
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: loginEmail.trim(),
        password: loginPassword,
      });
      if (error) throw error;
      toast.success("Успешный вход!");
      handleSuccess();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Ошибка входа";
      toast.error(translateError(message));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailError = validateEmail(registerEmail);
    if (emailError) {
      toast.error(emailError);
      registerEmailRef.current?.focus();
      return;
    }
    if (registerPassword.length < 6) {
      toast.error("Пароль должен быть минимум 6 символов");
      return;
    }
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: registerEmail.trim(),
        password: registerPassword,
      });
      if (error) throw error;
      if (data.user) {
        if (data.user.identities?.length === 0) {
          toast.error("Этот email уже зарегистрирован. Попробуйте войти.");
          setActiveTab("login");
          setLoginEmail(registerEmail);
        } else if (data.session) {
          toast.success("Аккаунт создан! Бесплатные кредиты добавлены на баланс.");
          // registration goal is now fired server-side (with yclid attribution)
          // from POST /user/track — covers both email + OAuth signups.
          setRegisterEmail("");
          setRegisterPassword("");
          handleSuccess();
        } else {
          toast.success("Аккаунт создан! Войдите с вашим паролем.");
          setRegisterPassword("");
          setActiveTab("login");
          setLoginEmail(registerEmail);
        }
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Ошибка регистрации";
      toast.error(translateError(message));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailError = validateEmail(resetEmail);
    if (emailError) {
      toast.error(emailError);
      resetEmailRef.current?.focus();
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail.trim(), {
        // `?type=recovery` is preserved through GoTrue's verify→redirect_to
        // (it appends `&code=…`), so /auth/confirm can distinguish recovery
        // from a regular OAuth callback. Without it PKCE-flow swallows the
        // recovery purpose and the user just auto-signs-in instead of being
        // shown the new-password form.
        redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
      });
      if (error) throw error;
      toast.success("Письмо отправлено. Ссылка действительна 24 часа.");
      setShowReset(false);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Ошибка отправки";
      toast.error(translateError(message));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ResponsiveDialog open={isOpen} onOpenChange={(open) => !open && close()} title="Авторизация" className="max-w-[95vw] sm:max-w-md p-0 bg-card/95 border-border backdrop-blur-xl text-card-foreground overflow-hidden">
        {/* Header with motivation */}
        <div className="px-6 pt-6 pb-2 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <svg width="32" height="32" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0" aria-hidden="true">
              <rect width="512" height="512" rx="96" className="fill-primary" />
              <path d="M120 132 L392 132 L392 204 L296 204 L296 380 L216 380 L216 204 L120 204 Z" fill="#000" />
            </svg>
            <span className="text-xl font-bold">Творикс</span>
          </div>
          {message ? (
            <p className="text-sm text-muted-foreground">{message}</p>
          ) : (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Gift className="h-4 w-4 text-primary" />
              <span>Зарегистрируйтесь и получите бесплатные кредиты</span>
            </div>
          )}
        </div>

        <div className="px-6 pb-6">
          {showReset ? (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  ref={resetEmailRef}
                  id="reset-email"
                  type="email"
                  placeholder="email@example.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  className="bg-muted/50 border-border"
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Отправка..." : "Отправить ссылку для сброса"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full text-muted-foreground hover:text-foreground"
                onClick={() => setShowReset(false)}
              >
                Вернуться ко входу
              </Button>
            </form>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-muted/50">
                <TabsTrigger value="login">Вход</TabsTrigger>
                <TabsTrigger value="register">Регистрация</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="modal-login-email">Email</Label>
                    <Input
                      ref={loginEmailRef}
                      id="modal-login-email"
                      type="email"
                      placeholder="email@example.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      className="bg-muted/50 border-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="modal-login-password">Пароль</Label>
                      <button
                        type="button"
                        className="text-xs text-primary hover:text-primary/80 transition-colors"
                        onClick={() => {
                          setResetEmail(loginEmail);
                          setShowReset(true);
                        }}
                      >
                        Забыли пароль?
                      </button>
                    </div>
                    <Input
                      id="modal-login-password"
                      type="password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      className="bg-muted/50 border-border"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Вход..." : "Войти"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="modal-register-email">Email</Label>
                    <Input
                      ref={registerEmailRef}
                      id="modal-register-email"
                      type="email"
                      placeholder="email@example.com"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      required
                      className="bg-muted/50 border-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="modal-register-password">Пароль</Label>
                    <Input
                      id="modal-register-password"
                      type="password"
                      placeholder="Минимум 6 символов"
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      required
                      minLength={6}
                      className="bg-muted/50 border-border"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Создание..." : "Создать аккаунт"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          )}

          {!showReset && <OAuthButtons isLoading={isLoading} dividerBg="bg-card" />}
        </div>

        <div className="px-6 pb-4 text-center text-xs text-muted-foreground">
          Продолжая, вы соглашаетесь с условиями использования
        </div>
    </ResponsiveDialog>
  );
}
